import { all, put, select, takeLatest } from 'redux-saga/effects';
import { FinancialActionTypes } from './financials-actions';
import { AlertActionType } from '@store/alert/alert-action';
import discountValidation from '@api/financials/discount-validation';
import orderItem from '@api/financials/order-item';
import sendOrder from '@api/financials/send-order';
import { financeStore } from './financials-store';
import ReactGA from 'react-ga4';
import uploadCardToCardReceiptFileApi from '@api/financials/upload-card-to-card-receipt-file';
import storeCardToCardReceiptApi from '@api/financials/store-card-to-card-receipt';
import cancelOrder from '@api/financials/cancel-order';
import { homeStore } from '@store/home-screen/home-store';
import routes from '@routes';
import getParseRoute from '@utils/helpers/parse-route';
import getDictionary from '@dictionaries-client';

function* getOrderItemWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { financials: { messages } } } = getDictionary(locale);
  try {
    const { orderItemId } = yield select(financeStore);

    const orderItemResponse: { data: { data: { order_reference_id_affix: string, order_client_full_name: string, order_service_title: string, created_at_affix: string, order_client_address: string, price: number, payment_type: string, credit_price: number, payment_tracking_code: string, payment_status: string }, pay_routes: { way_route_1: { gateways: Array<object>, default_gateway: string }, card_to_card: any } } } = yield orderItem({
      orderItemId: orderItemId,
      cache: 'no-cache',
    });
    yield put({
      type: FinancialActionTypes.SET_ORDER_ITEM,
      data: {
        orderItemDetails: orderItemResponse?.data?.data,
        orderPaymentDetails: orderItemResponse?.data?.pay_routes?.way_route_1,
        cardToCardData: orderItemResponse?.data?.pay_routes?.card_to_card,
      },
    });
  } catch (error: any) {
    const { navigation, locale } = yield select(homeStore);
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.expire_link_error,
      severity: 'danger',
    });

    if (error?.code === 406) {
      yield navigation.replace(getParseRoute({ pathname: routes['route.order.expired'], locale: locale }));
    } else if (error?.code !== 401) {
      yield navigation.replace(getParseRoute({ pathname: routes['route.client.orders'], locale: locale }));
    }
  }
}

function* getCodeValidationWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { financials: { messages } } } = getDictionary(locale);
  try {
    const { discountCode, orderItemId } = yield select(financeStore);

    const discountValidationResponse: { data: { coupon_price: number } } = yield discountValidation({
      orderItemId: orderItemId,
      discountCode: discountCode,
      cache: 'no-cache',
    });

    yield put({
      type: FinancialActionTypes.SET_DISCOUNT_VALIDATION,
      data: {
        discountAmount: discountValidationResponse?.data?.coupon_price,
        discountCode: discountCode,
        discountValid: true,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: messages.discount_code_success,
      severity: 'success',
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.discount_code_error,
      severity: 'danger',
    });

    yield put({
      type: FinancialActionTypes.SET_DISCOUNT_VALIDATION,
      data: {
        discountAmount: 0,
        discountValid: false,
        discountCode: null,
      },
    });
  }
}

function* sendOrderItemWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { financials: { messages } } } = getDictionary(locale);
  try {
    const { navigation } = yield select(homeStore);
    const { orderItemId, selectedGateway, discountValid, walletSelected, discountCode, moduleName, isShop } = yield select(financeStore);
    const orderItemResponse: { data: {}, extra: { virtual_pay: string, redirect: string } } = yield sendOrder({
      orderItemId: orderItemId,
      gateway: selectedGateway,
      discountAllowed: discountValid,
      creditAllowed: walletSelected,
      discountCode: discountCode,
      cache: 'no-cache',
    });
    if (moduleName === 'pharmacy' && !isShop) {
      ReactGA.ga('send', 'event', 'Rx', 'Payment', 'go to rx payment');
    }
    if (moduleName === 'laboratory') {
      ReactGA.ga('send', 'event', 'laboratory', 'payment', 'Open the payment gateway page');
    }
    if (moduleName === 'consult') {
      ReactGA.ga('send', 'event', 'consult', 'payment', 'open the gateway page');
    }
    if (moduleName === 'pharmacy' && isShop) {
      ReactGA.ga('send', 'event', 'Product', 'Go to payment gateway', 'product go to payment gateway general');
    }

    if (!orderItemResponse?.extra?.virtual_pay) {
      yield navigation.push(orderItemResponse?.extra?.redirect);
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.payment_success,
        severity: 'success',
      });
      yield put({
        type: FinancialActionTypes.GET_ORDER_ITEM,
        data: {
          orderItemId: orderItemId,
        },
      });
    }
  } catch (error: any) {
    if (error?.code === 422) {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || messages.gateway_error,
        severity: 'danger',
      });
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || messages.gateway_error,
        severity: 'danger',
      });
    }
    yield put({
      type: FinancialActionTypes.SET_ORDER_LOADING,
      data: {
        paymentLoading: false,
      },
    });
  }
}

function* sendCardToCardReceiptOrderWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { financials: { messages } } } = getDictionary(locale);
  try {
    const { receiptFile, orderItemId } = yield select(financeStore);

    const uploadFileResponse: { data: any } = yield uploadCardToCardReceiptFileApi({ file: receiptFile, cache: 'no-cache' });

    if (uploadFileResponse) {
      yield storeCardToCardReceiptApi({
        orderItemId: orderItemId,
        uploadReceipt: {
          '0': uploadFileResponse,
        },
      });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.upload_receipt_success,
        severity: 'success',
      });
      yield put({ type: FinancialActionTypes.SET_IS_UPLOADED_RECEIPT });
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.upload_receipt_error,
        severity: 'danger',
      });
    }

    yield put({
      type: FinancialActionTypes.SET_ORDER_LOADING,
      data: {
        paymentLoading: false,
      },
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.store_prescription_error,
      severity: 'danger',
    });
    yield put({
      type: FinancialActionTypes.SET_ORDER_LOADING,
      data: {
        paymentLoading: false,
      },
    });
  }
}

function* sendCancelOrderWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { financials: { messages } } } = getDictionary(locale);
  try {
    const { navigation, isMobileRoute, locale } = yield select(homeStore);
    const { reasonId, orderItemId } = yield select(financeStore);

    yield cancelOrder({ reasonId: reasonId, orderItemId: orderItemId, cache: 'no-cache' });

    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: messages.cancel_order_success,
      severity: 'success',
    });
    yield put({
      type: FinancialActionTypes.SET_CANCEL_ORDER_LOADING,
      data: {
        cancelOrderLoading: false,
      },
    });
    if (isMobileRoute) {
      yield navigation.push(getParseRoute({ pathname: `/mobile${routes['route.client.orders']}`, locale: locale }));
    } else {
      yield navigation.push(getParseRoute({ pathname: routes['route.client.orders'], locale: locale }));
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.cancel_order_error,
      severity: 'danger',
    });
    yield put({
      type: FinancialActionTypes.SET_CANCEL_ORDER_LOADING,
      data: {
        cancelOrderLoading: false,
      },
    });
  }
}

function* financialsMiddleware() {
  yield all([
    takeLatest(FinancialActionTypes.GET_ORDER_ITEM, getOrderItemWatcher),
    takeLatest(FinancialActionTypes.GET_CODE_VALIDATION, getCodeValidationWatcher),
    takeLatest(FinancialActionTypes.SET_ORDER_PAYMENT, sendOrderItemWatcher),
    takeLatest(FinancialActionTypes.SEND_CARD_TO_CARD_RECEIPT_ORDER, sendCardToCardReceiptOrderWatcher),
    takeLatest(FinancialActionTypes.HANDLE_CANCEL_ORDER, sendCancelOrderWatcher),
  ]);
}

export default financialsMiddleware;
